import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, MapPin, Send, Building2, Loader2 } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { PageHero, Reveal, SectionTag } from "../components/shared";
import { COMPANY } from "../i18n/translations";
import { IMAGES } from "../constants/images";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", company: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return;
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(t.contact.success);
      setForm({ name: "", email: "", company: "", subject: "", message: "" });
    } catch (err) {
      toast.error(t.contact.error);
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "bg-[#0a0a0c] border-white/10 text-white placeholder:text-zinc-600 focus-visible:ring-[#1E90FF] focus-visible:ring-1 h-12";

  return (
    <div data-testid="contact-page">
      <PageHero tag={t.nav.contact} title={t.contact.heroTitle} subtitle={t.contact.heroSubtitle} image={IMAGES.luxembourg} />

      <section className="relative py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-5 gap-10">
          {/* FORM */}
          <Reveal className="lg:col-span-3">
            <div className="rounded-[2rem] border border-white/10 bg-[#0F0F12] p-8 md:p-10">
              <SectionTag>{t.contact.formTitle}</SectionTag>
              <form onSubmit={submit} className="mt-8 space-y-5" data-testid="contact-form">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div className="space-y-2">
                    <Label className="text-zinc-300 text-sm">{t.contact.name} *</Label>
                    <Input data-testid="contact-name-input" value={form.name} onChange={update("name")} required className={inputCls} placeholder={t.contact.name} />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-zinc-300 text-sm">{t.contact.email} *</Label>
                    <Input data-testid="contact-email-input" type="email" value={form.email} onChange={update("email")} required className={inputCls} placeholder="you@company.com" />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div className="space-y-2">
                    <Label className="text-zinc-300 text-sm">{t.contact.company}</Label>
                    <Input data-testid="contact-company-input" value={form.company} onChange={update("company")} className={inputCls} placeholder={t.contact.company} />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-zinc-300 text-sm">{t.contact.subject}</Label>
                    <Input data-testid="contact-subject-input" value={form.subject} onChange={update("subject")} className={inputCls} placeholder={t.contact.subject} />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300 text-sm">{t.contact.message} *</Label>
                  <Textarea data-testid="contact-message-input" value={form.message} onChange={update("message")} required rows={6} className="bg-[#0a0a0c] border-white/10 text-white placeholder:text-zinc-600 focus-visible:ring-[#1E90FF] focus-visible:ring-1 resize-none" placeholder={t.contact.message} />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="contact-submit-button"
                  className="inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full bg-[#1E90FF] hover:bg-[#3B82F6] text-white font-semibold transition-all duration-200 hover:-translate-y-0.5 shadow-[0_0_30px_rgba(30,144,255,0.35)] disabled:opacity-60 disabled:translate-y-0"
                >
                  {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
                  {loading ? t.contact.sending : t.contact.submit}
                </button>
              </form>
            </div>
          </Reveal>

          {/* INFO */}
          <Reveal delay={0.1} className="lg:col-span-2 space-y-6">
            <div className="rounded-3xl border border-white/10 bg-[#0F0F12] p-8">
              <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-[#1E90FF]/10 border border-[#1E90FF]/20 text-[#1E90FF]">
                <Mail className="h-6 w-6" />
              </div>
              <h3 className="mt-5 text-lg font-semibold text-white">{t.contact.emailLabel}</h3>
              <a href={`mailto:${COMPANY.email}`} data-testid="contact-email-address" className="mt-2 inline-block text-[#00E5FF] hover:underline font-mono text-sm">
                {COMPANY.email}
              </a>
            </div>

            <div className="rounded-3xl border border-white/10 bg-[#0F0F12] p-8">
              <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-[#00E5FF]/10 border border-[#00E5FF]/20 text-[#00E5FF]">
                <Building2 className="h-6 w-6" />
              </div>
              <h3 className="mt-5 text-lg font-semibold text-white">{t.contact.officesLabel}</h3>
              <ul className="mt-4 space-y-4">
                {t.global.offices.map((o, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <MapPin className="h-4 w-4 text-[#1E90FF] mt-1 shrink-0" />
                    <div>
                      <div className="text-white text-sm font-medium">
                        {o.city}
                        {i === 0 && <span className="ml-2 font-mono text-[10px] text-[#00E5FF]">HQ</span>}
                      </div>
                      <div className="text-xs text-zinc-500">{o.country}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
